import {
    OwaDate,
    startOfDay,
    owaDate,
    getDay,
    isAfter,
    addDays,
    differenceInDays,
} from 'owa-datetime';
import createAbsoluteMonthlyRecurrence from './createAbsoluteMonthlyRecurrence';
import createAbsoluteYearlyRecurrence from './createAbsoluteYearlyRecurrence';
import createDailyRecurrence from './createDailyRecurrence';
import createRecurrenceFromSelectedDays from './createRecurrenceFromSelectedDays';
import createRelativeMonthlyRecurrence from './createRelativeMonthlyRecurrence';
import createRelativeYearlyRecurrence from './createRelativeYearlyRecurrence';
import type EndDateRecurrence from 'owa-service/lib/contract/EndDateRecurrence';
import getRecurrenceInterval from './getRecurrenceInterval';
import getSelectedDaysFromRecurrence from './getSelectedDaysFromRecurrence';
import type RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import { END_DATE_RECURRENCE, DAYS_OF_WEEK } from './constants';
import {
    isAbsoluteMonthlyRecurrence,
    isAbsoluteYearlyRecurrence,
    isRelativeMonthlyRecurrence,
    isRelativeYearlyRecurrence,
    isWeeklyRecurrence,
    isDailyRecurrence,
} from './checkRecurrenceTypes';

export default function updateRecurrenceOnStartDateChange(
    newStartDate: OwaDate,
    recurrence: RecurrenceType
): RecurrenceType {
    let endDate: OwaDate = null;
    let recurrenceRange: EndDateRecurrence;
    let newRecurrence: RecurrenceType;
    let newStart = startOfDay(newStartDate);

    // Maintain existing end date if it is still after the start date,
    // otherwise update and maintain previous duration
    if (recurrence.RecurrenceRange.__type == END_DATE_RECURRENCE) {
        recurrenceRange = recurrence.RecurrenceRange as EndDateRecurrence;
        const end = owaDate(newStartDate, recurrenceRange.EndDate);
        endDate = startOfDay(end);

        if (isAfter(newStart, endDate)) {
            endDate = addDays(
                newStart,
                differenceInDays(
                    owaDate(newStartDate, recurrenceRange.EndDate),
                    owaDate(newStartDate, recurrenceRange.StartDate)
                )
            );
        }
    }

    // with every change of date, create new recurrence based on the new date
    if (isAbsoluteMonthlyRecurrence(recurrence)) {
        newRecurrence = createAbsoluteMonthlyRecurrence(
            newStart,
            getRecurrenceInterval(recurrence),
            endDate
        );
    } else if (isAbsoluteYearlyRecurrence(recurrence)) {
        newRecurrence = createAbsoluteYearlyRecurrence(newStart, endDate);
    } else if (isRelativeMonthlyRecurrence(recurrence)) {
        newRecurrence = createRelativeMonthlyRecurrence(
            newStart,
            getRecurrenceInterval(recurrence),
            endDate
        );
    } else if (isRelativeYearlyRecurrence(recurrence)) {
        newRecurrence = createRelativeYearlyRecurrence(newStart, endDate);
    } else if (isWeeklyRecurrence(recurrence)) {
        let selectedDays = getSelectedDaysFromRecurrence(recurrence);

        if (selectedDays.length === 1) {
            selectedDays = [DAYS_OF_WEEK[getDay(newStart)]];
        }

        newRecurrence = createRecurrenceFromSelectedDays(
            newStart,
            selectedDays,
            getRecurrenceInterval(recurrence),
            endDate
        );
    } else if (isDailyRecurrence(recurrence)) {
        newRecurrence = createDailyRecurrence(newStart, getRecurrenceInterval(recurrence), endDate);
    }

    return newRecurrence;
}
